import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import {
  LuRadio, LuBrainCircuit, LuZap, LuGlobe, LuScanEye,
  LuCloud, LuLock, LuChartBar, LuArrowRight, LuShieldCheck, LuSettings2,
  LuInfo,
} from 'react-icons/lu';

const IMG = {
  hero:     'https://i.pinimg.com/736x/52/fe/25/52fe25d6d5c0b34bcf269a3b334d16c9.jpg',
  rfid:     'https://i.pinimg.com/736x/f1/70/bc/f170bc5a15e3bb5924988bed6a757b91.jpg',
  ai:       'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=700&q=80&auto=format',
  iot:      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=700&q=80&auto=format',
  cloud:    'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80&auto=format',
  automation:'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=700&q=80&auto=format',
  sensors:  'https://images.unsplash.com/photo-1605379399642-870262d3d051?w=700&q=80&auto=format',
  security: 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=700&q=80&auto=format',
  analytics:'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80&auto=format',
};

const TECHS = [
  { icon:<LuRadio size={26}/>,        img: IMG.rfid,      title:'RFID Tracking',        desc:'Radio-frequency tags on bags are read from up to 5 m away without line of sight, enabling continuous real-time tracking across the entire airport network.' },
  { icon:<LuBrainCircuit size={26}/>, img: IMG.ai,        title:'Smart Sorting Systems',  desc:'Advanced routing algorithms analyse flight schedules, bag data, and real-time conditions to optimise baggage flow dynamically.' },
  { icon:<LuZap size={26}/>,          img: IMG.automation,title:'Full Automation',        desc:'Modern systems operate with zero human intervention from check-in to aircraft loading, dramatically reducing errors and increasing throughput capacity.' },
  { icon:<LuGlobe size={26}/>,        img: IMG.iot,       title:'IoT Monitoring',         desc:'Internet of Things sensors report belt health, motor temperature, throughput rates, and jam alerts 24/7 directly to operations centres worldwide.' },
  { icon:<LuScanEye size={26}/>,      img: IMG.sensors,   title:'Smart Sensors',          desc:'Laser, infrared, and weight sensors provide centimetre-level bag positioning data at every metre of the entire conveyor journey for precise tracking.' },
  { icon:<LuCloud size={26}/>,        img: IMG.cloud,     title:'Cloud Integration',      desc:'Cloud-based Baggage Management Systems (BMS) share data across airports, airlines, and ground handlers globally in real time with sub-second latency.' },
  { icon:<LuLock size={26}/>,         img: IMG.security,  title:'Cybersecurity',          desc:'Encrypted data channels and zero-trust architecture protect control systems from cyber threats, preventing unauthorised access or operational tampering.' },
  { icon:<LuChartBar size={26}/>,     img: IMG.analytics, title:'Predictive Analytics',   desc:'Historical sensor data feeds predictive models that flag components approaching failure up to 2 weeks before they cause any passenger-facing disruption.' },
];

const TIMELINE = [
  { year:'1960s', event:'First automated baggage conveyors introduced at major US airports, replacing labour-intensive manual trolley systems entirely.' },
  { year:'1980s', event:'Barcode scanning adopted widely — enabling the first digital tracking of individual bags through the entire journey.' },
  { year:'1990s', event:'Computer-controlled sorting systems replaced manual baggage routing, dramatically improving accuracy and throughput capacity.' },
  { year:'2000s', event:'RFID technology began supplementing barcode systems, improving read rates from 85% to near 99.9% at major hubs.' },
  { year:'2010s', event:'Machine learning integrated into baggage management systems for dynamic real-time routing optimisation.' },
  { year:'2020s', event:'IoT sensor networks, cloud-based BMS, and predictive maintenance become the global aviation industry standard.' },
];

const RFID_ROWS = [
  { label:'Read Rate',      barcode:'~85%',             rfid:'~99.9%' },
  { label:'Line of Sight',  barcode:'Required',         rfid:'Not needed' },
  { label:'Read Range',     barcode:'0 – 30 cm',        rfid:'Up to 5 m' },
  { label:'Read Speed',     barcode:'Single bag',       rfid:'Multiple simultaneous' },
  { label:'Data Capacity',  barcode:'Tag number only',  rfid:'Rich flight data set' },
  { label:'Cost Trend',     barcode:'Very low',         rfid:'Moderate (falling fast)' },
];

function Technology() {
  return (
    <div className="page-enter">
      <PageHero
        tag="Innovation"
        title={<>Technology Behind the <span className="text-gradient-2">System</span></>}
        subtitle="Today's airport baggage systems are marvels of modern engineering — combining AI, IoT, RFID, and cloud computing to deliver near-perfect reliability at massive scale."
        breadcrumb={[{ label:'Technology' }]}
        bgColor={`radial-gradient(ellipse 65% 55% at 65% 35%, rgba(8,145,178,.08) 0%, transparent 60%),
                  radial-gradient(ellipse 50% 40% at 20% 75%, rgba(124,58,237,.06) 0%, transparent 55%), var(--bg)`}
      >
        <div className="page-hero-img-card">
          <img src={IMG.hero} alt="Technology circuit board representing airport systems" loading="lazy"/>
        </div>
      </PageHero>

      {/* ── TECH CARDS ── */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom:48 }}>
            <div className="section-tag">Core Technologies</div>
            <h2 className="section-title">What Powers Modern Baggage Systems?</h2>
            <p className="section-sub" style={{ margin:'0 auto' }}>
              Eight cutting-edge technologies work together to make airport baggage handling
              fast, accurate, and resilient around the clock.
            </p>
          </div>
          <div className="tech-grid-cards">
            {TECHS.map((t,i) => (
              <div key={i} className={`tech-card-new reveal delay-${(i%6)+1}`}>
                {t.img && (
                  <div className="tech-card-img">
                    <img src={t.img} alt={t.title} loading="lazy"/>
                  </div>
                )}
                <div style={{ padding: t.img ? '18px 20px 20px' : '24px 20px 20px' }}>
                  <div className="tc-icon-wrap">{t.icon}</div>
                  <div className="tc-title">{t.title}</div>
                  <div className="tc-desc">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── RFID vs BARCODE ── */}
      <section className="page-section" id="rfid">
        <div className="container">
          <div className="grid-2">
            <div className="reveal-left">
              <div className="section-tag">Tracking</div>
              <h2 className="section-title">RFID vs Barcode: The Difference</h2>
              <p style={{ color:'var(--text-3)', lineHeight:1.85, marginBottom:22 }}>
                Both technologies identify bags, but RFID is dramatically more capable and is
                now replacing barcodes at most major international airports worldwide. IATA's
                Resolution 753 now mandates bag tracking at departure, transfer, and arrival.
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, padding:'8px 14px', fontSize:'.74rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--text-muted)' }}>
                  <span>Feature</span>
                  <span style={{ textAlign:'center', color:'var(--accent-2)' }}>Barcode</span>
                  <span style={{ textAlign:'center', color:'var(--primary)' }}>RFID</span>
                </div>
                {RFID_ROWS.map((row,i) => (
                  <div key={i} className="rfid-row">
                    <span className="rfid-label">{row.label}</span>
                    <span className="rfid-barcode">{row.barcode}</span>
                    <span className="rfid-rfid">{row.rfid}</span>
                  </div>
                ))}
              </div>

              <div className="highlight-box" style={{ marginTop:22, display:'flex', gap:10, alignItems:'flex-start' }}>
                <LuInfo size={18} style={{ flexShrink:0, marginTop:2, color:'var(--primary)' }}/>
                <span>
                  <strong>Industry fact:</strong> Airports that fully switched to RFID tracking
                  (such as Las Vegas McCarran) report mishandling rates below <strong>0.2 bags per 1,000 passengers</strong> — a 10× improvement over barcode-only systems.
                </span>
              </div>
            </div>

            <div className="reveal-right" style={{ display:'flex', flexDirection:'column', gap:18 }}>
              <div className="tech-compare-img">
                <img src={IMG.rfid} alt="RFID scanner on airport conveyor belt" loading="lazy"/>
                <div className="tech-compare-img-label">
                  <LuRadio size={14}/> Active RFID readers scanning bags in real time
                </div>
              </div>
              {[
                {
                  icon:<LuChartBar size={20}/>, title:'Barcode System',
                  color:'var(--accent-2)', bg:'rgba(219,39,119,.05)', border:'rgba(219,39,119,.18)',
                  points:['Requires direct line of sight to read','~85% average read rate at checkpoints','Lower initial implementation cost','Still used as a backup/fallback system','Single sequential checkpoint scanning'],
                },
                {
                  icon:<LuRadio size={20}/>, title:'RFID System',
                  color:'var(--primary)', bg:'var(--primary-pale)', border:'rgba(124,58,237,.2)',
                  points:['No line of sight required at any point','99.9%+ read accuracy system-wide','Real-time continuous bag tracking','Read multiple bags simultaneously','Rich passenger & flight data stored'],
                },
              ].map((card,i) => (
                <div key={i} style={{ background:card.bg, border:`1px solid ${card.border}`, borderRadius:16, padding:20, boxShadow:'var(--shadow-sm)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                    <span style={{ color:card.color }}>{card.icon}</span>
                    <span style={{ fontSize:'.93rem', fontWeight:700, color:'var(--text-2)' }}>{card.title}</span>
                  </div>
                  {card.points.map((p,j) => (
                    <div key={j} style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:'.82rem', color:'var(--text-3)', marginBottom:6 }}>
                      <span style={{ color:card.color, fontSize:'.6rem', marginTop:5 }}>●</span>{p}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── TIMELINE ── */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom:52 }}>
            <div className="section-tag">History</div>
            <h2 className="section-title">Evolution of Baggage Technology</h2>
            <p className="section-sub" style={{ margin:'0 auto' }}>
              Six decades of continuous innovation have transformed baggage handling from
              manual trolleys to fully automated AI-driven systems.
            </p>
          </div>
          <div className="timeline-wrap">
            {TIMELINE.map((t,i) => (
              <div key={i} className={`timeline-item reveal delay-${i+1}`}>
                <div className="timeline-spine">
                  <div className="timeline-dot"/>
                  {i<TIMELINE.length-1 && <div className="timeline-line"/>}
                </div>
                <div className="timeline-content">
                  <div className="timeline-year">{t.year}</div>
                  <div className="timeline-event">{t.event}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* ── CTA ── */}
      <section className="page-section-sm">
        <div className="container text-center reveal">
          <h2 className="section-title">Explore More About the System</h2>
          <p className="section-sub" style={{ margin:'0 auto 32px' }}>
            Understand how safety systems and emergency backups keep these technologies running reliably.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/safety" className="btn btn-primary">
              <LuShieldCheck size={17}/> Safety Systems
            </Link>
            <Link to="/how-it-works" className="btn btn-outline">
              <LuSettings2 size={17}/> How It Works <LuArrowRight size={15}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Technology;
